import { useState, useMemo } from "react";
import { Box, Button, Divider, Grid, Typography } from "@mui/material";
import type {
  RequestedPicturesPlotDataResponse,
  RequestedPicturesHistoryItem,
} from "../../types";
import { DetailRow } from "./DetailRow";
import { compareLatestRequest } from "./RealEstateDetailDialog";
import { formatUpdateTime } from "./utils";
import { AllItemsDialog } from "./AllItemsDialog";

interface MortgageesSectionProps {
  plot: RequestedPicturesPlotDataResponse;
  historyData?: RequestedPicturesHistoryItem;
  isLoadingHistory?: boolean;
}

/**
 * 抵当権者（土地）セクション
 */
export const MortgageesSection = ({
  plot,
  historyData,
  isLoadingHistory,
}: MortgageesSectionProps): JSX.Element => {
  const [showAllMortgageesDialog, setShowAllMortgageesDialog] =
    useState<boolean>(false);

  const allMortgagees = useMemo(() => {
    // historyDataがある場合はmortgageesを使用
    if (historyData) {
      return historyData.mortgagees.map((mortgagee) => ({
        acceptanceDate: formatUpdateTime(mortgagee.acceptanceDate) ?? "-",
        purposeOfRegistration: mortgagee.purposeOfRegistration ?? "-",
        mortgageeName: mortgagee.mortgageeName ?? "-",
      }));
    }

    // historyDataがない場合は従来のロジックを使用
    if (compareLatestRequest(plot) === "ALL_MATTERS") {
      const mortgagee = plot.allMatter?.mortgagee;
      return mortgagee
        ? [
            {
              acceptanceDate: formatUpdateTime(mortgagee.acceptanceDate) ?? "-",
              purposeOfRegistration: mortgagee.purposeOfRegistration ?? "-",
              mortgageeName: mortgagee.mortgageeName ?? "-",
            },
          ]
        : [];
    }
    return [];
  }, [plot, historyData]);

  const latestAllMatter = useMemo(() => {
    return compareLatestRequest(plot) === "ALL_MATTERS"
      ? plot.allMatter
      : undefined;
  }, [plot]);

  const pictureUpdateTime = latestAllMatter?.pictureUpdateTime ?? "-";
  const mortgageeName = latestAllMatter?.mortgagee?.mortgageeName ?? "-";
  const purposeOfRegistration =
    latestAllMatter?.mortgagee?.purposeOfRegistration ?? "-";

  return (
    <>
      <Divider sx={{ my: 2 }} />
      <Box component="section" aria-label="抵当権者情報">
        <Typography sx={{ mb: 2, fontWeight: 700 }}>
          抵当権者（土地）
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={10} md={10}>
            <Typography variant="body2" sx={{ color: "error.main" }}>
              ※抵当権者は、代表者1名のみを表示いたします
            </Typography>
            <Typography variant="body2" mt={1} sx={{ fontWeight: 700 }}>
              最新全部事項取得日: {formatUpdateTime(pictureUpdateTime)}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={2} md={2}>
            <Button
              variant="outlined"
              size="small"
              onClick={() => {
                setShowAllMortgageesDialog(true);
              }}
              disabled={isLoadingHistory}
            >
              全て表示する
            </Button>
          </Grid>
          <DetailRow label="登記目的" value={purposeOfRegistration} />
          <DetailRow label="抵当権者" value={mortgageeName} />
        </Grid>
      </Box>
      <AllItemsDialog
        open={showAllMortgageesDialog}
        onClose={() => {
          setShowAllMortgageesDialog(false);
        }}
        title="抵当権者一覧"
        items={allMortgagees}
        fieldLabels={{
          acceptanceDate: "受付日",
          mortgageeName: "抵当権者",
          purposeOfRegistration: "登記目的",
        }}
        isLoading={isLoadingHistory}
      />
    </>
  );
};
